import { useState, useEffect, useRef } from 'react';
import actions from '@store/actions/tourbooks';
import classnames from 'classnames';
import { TourbookSummary, SearchListing, Listing, StoreState, BuildingListing } from '@root/types';
import { tourbookSummariesLoaded } from '@store/selectors';
import { connect, ConnectedProps } from 'react-redux';
import ToggleCard from './ToggleCard';
import CreateTourbookCard from './CreateTourbookCard';
import InfoCard from './InfoCard';
import s from './TourbookOverlay.module.less';

type FormValues = { name: string };

const mapState = (state: StoreState) => ({
  summariesLoaded: tourbookSummariesLoaded(state),
});
const mapDispatch = {
  createTourbook: (
    values: FormValues & { listingId: string; listing: SearchListing | Listing },
    { onSuccess, onError },
  ) => actions.createTourbook(values, { onSuccess, onError }),
  addListingToTourbook: (args: {
    listingId: string;
    tourbookId: string;
    onSuccess: (t: TourbookSummary) => void;
  }) => actions.createTourbookListing(args),
  removeListingFromTourbook: (args: {
    tourbookId: string;
    listingId: string;
    onSuccess: (t: TourbookSummary) => void;
  }) => actions.deleteTourbookListing({ ...args, external: false }),
  seeTourbookExplanation: () => actions.seeTourbookExplanation(),
};

const connector = connect(mapState, mapDispatch);
type ReduxProps = ConnectedProps<typeof connector>;

type Props = {
  isOpen: boolean;
  close: () => void;
  listing?: SearchListing | Listing | BuildingListing;
  createOnly?: boolean;
  tourbooks?: TourbookSummary[];
  className?: string | null;
};

type DialogCards = 'toggle' | 'createTourbook' | 'info' | 'loading';
export const RawTourbookOverlay = ({
  isOpen,
  close,
  tourbooks = [],
  listing,
  createTourbook,
  addListingToTourbook,
  removeListingFromTourbook,
  seeTourbookExplanation,
  createOnly = false,
  className,
  summariesLoaded,
}: Props & ReduxProps) => {
  const chooseCorrectStartingCard = (): DialogCards => {
    if (createOnly) return 'createTourbook';
    if (!summariesLoaded) return 'loading';
    return tourbooks.length > 0 ? 'toggle' : 'createTourbook';
  };
  const [activeCard, setActiveCard] = useState<DialogCards>(chooseCorrectStartingCard);
  const goToCreateCard = () => {
    setActiveCard('createTourbook');
  };
  const goToToggleCard = () => {
    setActiveCard('toggle');
  };
  const goToInfoCard = () => {
    setActiveCard('info');
    seeTourbookExplanation();
  };
  const closeAndReset = () => {
    close();
  };

  useEffect(() => {
    setActiveCard(chooseCorrectStartingCard());
  }, [createOnly, summariesLoaded]);

  const onCreate = (values, { onSuccess, onError }) => {
    if (createOnly) {
      createTourbook(values, {
        onSuccess: () => {
          onSuccess();
          closeAndReset();
        },
        onError,
      });
    } else {
      createTourbook(values, { onSuccess, onError });
    }
  };

  const dialog = useRef<HTMLDialogElement>(null!);
  const handleKeyDown = event => {
    if (event.key === 'Escape') closeAndReset();
  };
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return function cleanup() {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dialog]);

  useEffect(() => {
    const handleOutsideClick = event => {
      if (dialog && !dialog.current?.contains(event.target)) {
        closeAndReset();
      }
    };
    document.addEventListener('mouseup', handleOutsideClick);
    return () => {
      document.removeEventListener('mouseup', handleOutsideClick);
    };
  }, [dialog]);

  return (
    <dialog
      open={isOpen}
      className={classnames(s.overlay, isOpen && s.open, className)}
      data-testid="tourbookDialog"
      ref={dialog}
    >
      {activeCard === 'toggle' && !createOnly && !!listing ? (
        <ToggleCard
          tourbooks={tourbooks}
          addListingToTourbook={addListingToTourbook}
          removeListingFromTourbook={removeListingFromTourbook}
          close={closeAndReset}
          goToCreateCard={goToCreateCard}
          listing={listing}
        />
      ) : null}
      {activeCard === 'createTourbook' ? (
        <CreateTourbookCard
          close={closeAndReset}
          goToInfoCard={goToInfoCard}
          goToToggleCard={goToToggleCard}
          listing={listing}
          createTourbook={onCreate}
          createOnly={createOnly}
        />
      ) : null}
      {activeCard === 'info' ? (
        <InfoCard close={closeAndReset} goToCreateCard={goToCreateCard} />
      ) : null}
    </dialog>
  );
};

export default connector(RawTourbookOverlay);
